/* eslint-disable react/no-array-index-key */
import React, { Fragment } from 'react'
import { Listbox, Transition } from '@headlessui/react'
import {
  CheckIcon,
  ChevronUpDownIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/solid'
import { cn } from 'cn'
import isEqual from 'lodash/isEqual'
import FieldDescription from 'components/ui/FieldDescription'
import FieldError from 'components/ui/FieldError'

export type PossibleValue = string | number | object | boolean

export type Option<T extends PossibleValue> = {
  label: React.ReactElement | string
  value: T
}

export type Props<T extends PossibleValue = string> = {
  label?: string
  description?: string
  name: string
  options: Option<T>[]
  value: T | null
  onChange: (value: T) => void
  error?: string | null
  placeholder?: string
  newDesign?: boolean
}
export default function SelectField<T extends PossibleValue = string>({
  label,
  description,
  placeholder,
  error,
  options,
  value,
  name,
  onChange,
  newDesign
}: Props<T>) {
  return (
    <div className="relative">
      <Listbox name={name} onChange={onChange} value={value ?? undefined}>
        {({ open }) => (
          <>
            <Listbox.Label
              className={cn('block', {
                'text-sm font-medium text-gray-700': !newDesign,
                'font-theme-heading font-bold text-gray-900': newDesign
              })}
            >
              {label}
            </Listbox.Label>
            <div className="mt-1 relative">
              <Listbox.Button
                className={cn(
                  'h-12 relative w-full bg-white border  rounded-xl shadow-xs pl-3 pr-10 py-3 text-left cursor-default focus:outline-hidden focus:ring-1',
                  {
                    'border-gray-300 focus:ring-orange-500 focus:border-orange-700':
                      !error,
                    'border-red-300': error
                  }
                )}
              >
                {typeof value !== 'undefined' ? (
                  <span className="block truncate">
                    {options.find((o) => isEqual(o.value, value))?.label}
                  </span>
                ) : (
                  <span className="text-gray-400 font-light">
                    {placeholder}
                  </span>
                )}

                <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  {error && (
                    <ExclamationCircleIcon
                      aria-hidden="true"
                      className="h-5 w-5 mr-1 text-red-500"
                    />
                  )}
                  <ChevronUpDownIcon
                    aria-hidden="true"
                    className="h-5 w-5 text-gray-400"
                  />
                </span>
              </Listbox.Button>

              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                show={open}
              >
                <Listbox.Options className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-xl py-1 text-base ring-1 ring-black/5 overflow-auto focus:outline-hidden">
                  {options.map((option, index) => (
                    <Listbox.Option
                      key={`${option.label.toString()}-${index}`}
                      className={({ active }) =>
                        cn(
                          active ? 'text-white bg-orange-400' : 'text-gray-900',
                          'cursor-default select-none relative py-2 pl-8 pr-4'
                        )
                      }
                      value={option.value}
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={cn(
                              selected ? 'font-semibold' : 'font-normal',
                              'block truncate'
                            )}
                          >
                            {option.label}
                          </span>

                          {selected ? (
                            <span
                              className={cn(
                                active ? 'text-white' : 'text-orange-600',
                                'absolute inset-y-0 left-0 flex items-center pl-1.5'
                              )}
                            >
                              <CheckIcon
                                aria-hidden="true"
                                className="h-5 w-5"
                              />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
      <FieldError>{error}</FieldError>
      <FieldDescription newDesign={newDesign}>{description}</FieldDescription>
    </div>
  )
}
